import { readFileSync, statSync } from "node:fs";
import { visit } from "unist-util-visit";
import { parseMarkdown, getHeadingAtLine, isNegatedSection } from "../markdown.js";
import type { Claim } from "../types.js";
import type { Code, InlineCode, ListItem, Strong, TableCell, Text } from "mdast";
import { KNOWN_EXTENSIONS } from "../utils/extensions.js";
const COMMAND_PREFIXES = /^(npm|yarn|pnpm|bun|make|cargo|python|pip|go|node|npx|tsx|dotnet|mvn|gradle|\.\/gradlew|rake|bundle|mix|swift|rustup|dotnet-script)\s/;
const DEPENDENCY_SECTION_PATTERNS = /key\s*libraries|core\s*technologies|dependencies|stack|tech/i;
/** Paths with angle brackets or square brackets are template placeholders, not real paths */
const TEMPLATE_PLACEHOLDER = /[<>\[\]{}]/;
const COMMON_NON_PACKAGE_TERMS = new Set([
  "rest api",
  "websocket",
  "oauth",
  "frontend",
  "backend",
  "database",
  "database layer",
  "service layer",
  "tailwind css",
  // Protocols
  "http", "https", "grpc", "graphql", "sse", "mqtt", "amqp",
  // Architecture
  "microservices", "monolith", "serverless", "edge functions",
  "cdn", "api gateway", "reverse proxy", "load balancer",
  "event sourcing", "cqrs", "ddd",
  // Common infra terms that appear bolded in docs
  "api", "sdk", "cli", "ui",
]);

/** Patterns that indicate a mention is comparative/negative, not a positive dependency claim */
const COMPARISON_NEGATION = /\b(vs\.?|versus|instead of|alternative to|rather than|prefer\w* over|replaced by|not use|not using|excluded|deprecated|removed|considered|evaluated|compared to)\b/i;

/** HTTP methods that indicate an API route, not a file path */
const HTTP_METHOD_PREFIX = /^(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)\s+\//;
const PLACEHOLDER_WORDS =
  /(?:^|[/_-])(?:new|example|your|sample|my|foo|bar|placeholder|template)(?:[/_.-]|$)/i;

/** Things that look like paths but are actually code snippets, URL routes, or other non-path content */
function isNotAPath(value: string): boolean {
  // URL routes: /voice/incoming, /api/users — start with / but have no file extension
  if (value.startsWith("/") && !KNOWN_EXTENSIONS.test(value)) return true;

  // HTTP method + route: GET /api/bookmarks, POST /users/:id
  if (HTTP_METHOD_PREFIX.test(value)) return true;

  // URLs and module paths with a scheme (https://) or domain prefix (github.com/foo)
  if (value.includes("://")) return true;
  // Domain-style prefixes: github.com/foo, pkg.go.dev/foo — not filesystem paths
  if (/^[a-z0-9-]+\.[a-z]{2,}\//.test(value)) return true;

  // Compound shell commands: cd foo && cargo build
  if (/&&|\|\|/.test(value)) return true;

  // Home-directory paths: ~/foo — shell expansions, not repo paths
  if (value.startsWith("~/")) return true;

  // Code snippets: contains =, (), ;, or other code-like characters
  if (/[=();,]/.test(value)) return true;

  // Quoted strings or attribute assignments: gather.action="...", foo="bar"
  if (/["']/.test(value)) return true;

  // Glob patterns: context/*.md, **/*.ts — contain wildcard characters
  if (/[*?{]/.test(value)) return true;

  // Extension-only values: .js, .ts, .md — not real paths
  if (/^\.[a-z]{1,6}$/.test(value)) return true;

  // Runtime-generated artifacts that only exist after running cai commands — never flag as missing
  if (value.startsWith(".cai/.cache/") || value.startsWith(".context-condensing/.cache/")) return true;
  if (value.startsWith(".cai/codex/") || value.startsWith(".context-condensing/codex/")) return true;

  // CAI marker syntax: cai:start/end, cai:learn-start/end — URI-style, not file paths
  if (/^[a-z][\w-]*:[a-z]/.test(value)) return true;

  // Alias-only prefixes: @/, @components/ — path alias roots, no file component
  if (/^@[^/]*\/$/.test(value)) return true;

  // Bare top-level directory names: src/, test/, checkers/ (no nested path) — too ambiguous
  if (/^[a-z][\w-]*\/$/.test(value)) return true;

  return false;
}

/** In-process LRU cache: key = `${filePath}:${mtimeMs}` → Claim[].
 *  Capped at 500 entries to prevent unbounded growth in long-running processes (cai watch). */
const CLAIMS_CACHE_MAX = 500;
const claimsCache = new Map<string, Claim[]>();

/** Extract all claims from a markdown file */
export function extractClaims(filePath: string, source: string): Claim[] {
  // Cache by file path + mtime so stale data is never returned
  let mtimeMs = 0;
  try { mtimeMs = statSync(filePath).mtimeMs; } catch { return []; }
  const cacheKey = `${filePath}:${mtimeMs}`;
  const cached = claimsCache.get(cacheKey);
  if (cached) {
    // Move to end (LRU refresh) — Map preserves insertion order
    claimsCache.delete(cacheKey);
    claimsCache.set(cacheKey, cached);
    return cached;
  }

  let content: string;
  try {
    content = readFileSync(filePath, "utf-8");
  } catch {
    return [];
  }

  const tree = parseMarkdown(content);
  const claims: Claim[] = [];

  // Extract from inline code
  visit(tree, "inlineCode", (node: InlineCode) => {
    const line = node.position?.start.line ?? 0;
    const heading = getHeadingAtLine(tree, line);
    const negated = isNegatedSection(heading);

    // Path claims: contains / or ends in known extension
    if (node.value.includes("/") || KNOWN_EXTENSIONS.test(node.value)) {
      // Skip commands, template placeholders, and non-path content
      if (!COMMAND_PREFIXES.test(node.value) && !TEMPLATE_PLACEHOLDER.test(node.value) && !isNotAPath(node.value)) {
        claims.push(
          createClaim("path", node.value, source, line, heading, negated, "inline_code")
        );
      }
    }

    // Command claims
    if (COMMAND_PREFIXES.test(node.value)) {
      claims.push(
        createClaim("command", node.value, source, line, heading, negated, "inline_code")
      );
    }
  });

  // Extract from code blocks
  visit(tree, "code", (node: Code) => {
    const line = node.position?.start.line ?? 0;
    const heading = getHeadingAtLine(tree, line);
    const negated = isNegatedSection(heading);

    // Each line of the code block could be a command
    for (const codeLine of node.value.split("\n")) {
      const trimmed = codeLine.trim();
      if (COMMAND_PREFIXES.test(trimmed)) {
        claims.push(
          createClaim("command", trimmed, source, line, heading, negated, "code_block")
        );
      }
    }
  });

  // Extract dependencies from **BoldName** patterns in relevant sections
  const contentLines = content.split("\n");
  visit(tree, "strong", (node: Strong) => {
    const line = node.position?.start.line ?? 0;
    const heading = getHeadingAtLine(tree, line);
    const negated = isNegatedSection(heading);

    if (heading && DEPENDENCY_SECTION_PATTERNS.test(heading)) {
      const text = getStrongText(node);
      if (!text || !looksLikeDependencyClaim(text)) return;

      // Check surrounding line for comparison/negation language
      // e.g. "**Express** vs **Fastify**" or "alternative to **Redux**"
      const sourceLine = contentLines[line - 1] ?? "";
      const isComparison = COMPARISON_NEGATION.test(sourceLine);

      // Check for version pattern: "React 18" or "Node v20"
      const versionMatch = text.match(/^(.+?)\s+[v^~>=<]*(\d[\d.]*\S*)$/);
      if (versionMatch) {
        claims.push(
          createClaim("dependency", versionMatch[1].trim(), source, line, heading, negated || isComparison, "strong_text")
        );
        claims.push(
          createClaim("version", text, source, line, heading, negated || isComparison, "strong_text")
        );
      } else {
        claims.push(
          createClaim("dependency", text, source, line, heading, negated || isComparison, "strong_text")
        );
      }
    }
  });

  // Extract dependency/version claims from markdown tables in relevant sections
  visit(tree, "tableCell", (node: TableCell) => {
    const line = node.position?.start.line ?? 0;
    const heading = getHeadingAtLine(tree, line);
    const negated = isNegatedSection(heading);

    if (!heading || !DEPENDENCY_SECTION_PATTERNS.test(heading)) return;

    const text = getNodeText(node).trim();
    if (!text || !looksLikeDependencyClaim(text)) return;

    const versionMatch = text.match(/^(.+?)\s+[v^~>=<]*(\d[\d.]*\S*)$/);
    if (versionMatch) {
      claims.push(
        createClaim("dependency", versionMatch[1].trim(), source, line, heading, negated, "table_cell")
      );
      claims.push(
        createClaim("version", text, source, line, heading, negated, "table_cell")
      );
      return;
    }

    claims.push(
      createClaim("dependency", text, source, line, heading, negated, "table_cell")
    );
  });

  // Extract dependency/version claims from plain list items in relevant sections
  visit(tree, "listItem", (node: ListItem) => {
    const line = node.position?.start.line ?? 0;
    const heading = getHeadingAtLine(tree, line);
    const negated = isNegatedSection(heading);

    if (!heading || !DEPENDENCY_SECTION_PATTERNS.test(heading)) return;
    if (containsNodeType(node, "strong")) return;

    const text = extractDependencyCandidate(getNodeText(node));
    if (!text || !looksLikeDependencyClaim(text)) return;

    const versionMatch = text.match(/^(.+?)\s+[v^~>=<]*(\d[\d.]*\S*)$/);
    if (versionMatch) {
      claims.push(
        createClaim("dependency", versionMatch[1].trim(), source, line, heading, negated, "list_item")
      );
      claims.push(
        createClaim("version", text, source, line, heading, negated, "list_item")
      );
      return;
    }

    claims.push(
      createClaim("dependency", text, source, line, heading, negated, "list_item")
    );
  });

  // Evict oldest entries if cache exceeds limit
  if (claimsCache.size >= CLAIMS_CACHE_MAX) {
    const first = claimsCache.keys().next().value;
    if (first !== undefined) claimsCache.delete(first);
  }
  claimsCache.set(cacheKey, claims);
  return claims;
}

function getStrongText(node: Strong): string | null {
  const textNode = node.children.find(
    (c): c is Text => c.type === "text"
  );
  return textNode?.value ?? null;
}

function getNodeText(node: { children?: unknown[] }): string {
  if (!node.children) return "";
  const values: string[] = [];

  for (const child of node.children) {
    if (!child || typeof child !== "object" || !("type" in child)) continue;

    if ("value" in child && typeof child.value === "string") {
      values.push(child.value);
      continue;
    }

    if ("children" in child && Array.isArray(child.children)) {
      values.push(getNodeText(child as { children?: unknown[] }));
    }
  }

  return values.join(" ").replace(/\s+/g, " ").trim();
}

function containsNodeType(node: { children?: unknown[] }, type: string): boolean {
  if (!node.children) return false;

  for (const child of node.children) {
    if (!child || typeof child !== "object" || !("type" in child)) continue;
    if (child.type === type) return true;
    if (
      "children" in child &&
      Array.isArray(child.children) &&
      containsNodeType(child as { children?: unknown[] }, type)
    ) {
      return true;
    }
  }

  return false;
}

function extractDependencyCandidate(text: string): string {
  return text
    .split(/\s+[-:—]\s+/)[0]
    ?.trim()
    .replace(/\.$/, "") ?? "";
}

function looksLikeDependencyClaim(text: string): boolean {
  const normalized = text.trim().replace(/\s+/g, " ");
  const lower = normalized.toLowerCase();

  if (COMMON_NON_PACKAGE_TERMS.has(lower)) return false;
  if (/^[A-Z0-9\s.+-]+$/.test(normalized) && !/[a-z]/.test(normalized)) return false;

  const versionless = normalized.replace(/\s+[v^~>=<]*(\d[\d.]*\S*)$/, "");
  const words = versionless.split(" ");
  if (words.length > 1 && !/[@/._-]/.test(versionless)) return false;

  if (/layer|architecture|protocol/i.test(versionless)) return false;

  return true;
}

function createClaim(
  kind: Claim["kind"],
  value: string,
  source: string,
  line: number,
  section: string | null,
  negated: boolean,
  origin: Claim["origin"]
): Claim {
  const { intent, confidence } = classifyClaim(kind, value, origin, section);
  return {
    kind,
    value,
    source,
    line,
    section,
    negated,
    origin,
    intent,
    confidence,
  };
}

/** Headings that indicate canonical/authoritative instructions */
const CANONICAL_HEADING = /\b(setup|install|build|deploy|run|development|getting\s*started|quick\s*start|usage|commands)\b/i;
/** Headings that indicate examples, alternatives, or non-canonical content */
const EXAMPLE_HEADING = /\b(example|alternative|optional|comparison|deprecated)\b/i;

/** A naming-convention section lists filename patterns, not real path references */
const NAMING_HEADING = /\bnaming\b/i;

function classifyClaim(
  kind: Claim["kind"],
  value: string,
  origin: Claim["origin"],
  section: string | null
): Pick<Claim, "intent" | "confidence"> {
  if (kind === "path") {
    if (PLACEHOLDER_WORDS.test(value)) {
      return { intent: "example", confidence: "low" };
    }
    // A "## Naming" section lists filename *patterns* (kebab-case examples),
    // not real path references — treat those as illustrative, not missing files.
    if (section != null && NAMING_HEADING.test(section)) {
      return { intent: "example", confidence: "low" };
    }
    return {
      intent: "exact",
      confidence: value.includes("/") ? "high" : "medium",
    };
  }

  if (kind === "command") {
    if (origin === "code_block") {
      const isCanonical = section != null && CANONICAL_HEADING.test(section) && !EXAMPLE_HEADING.test(section);
      const isExample = section != null && EXAMPLE_HEADING.test(section);

      if (isCanonical) return { intent: "exact", confidence: "high" };
      if (isExample) return { intent: "example", confidence: "low" };
      return { intent: "example", confidence: "medium" };
    }
    return { intent: "exact", confidence: "high" };
  }

  if (kind === "dependency") {
    return {
      intent: "exact",
      confidence: origin === "strong_text" ? "high" : "medium",
    };
  }

  return {
    intent: "exact",
    confidence: origin === "strong_text" ? "high" : "medium",
  };
}
